import {
  Card,
  CardBody,
  Flex,
  Image,
  Stack,
  Text,
  Badge,
  Box,
} from "@chakra-ui/react";
import React, { useState } from "react";
import CurrencyRupeeTwoToneIcon from "@mui/icons-material/CurrencyRupeeTwoTone";
import { Link } from 'react-router-dom';
import './ProductCard.css'; // Import the custom CSS file

export default function ProductCard({ product }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Quality badge class
  const qualityClass = product.quality === 'A' ? 'product-quality-a' : 
                        product.quality === 'B' ? 'product-quality-b' : 
                        'product-quality-c';
  
  // Format price with unit
  const formatPriceWithUnit = () => {
    // Use priceUnit if available, otherwise fall back to unit
    const displayUnit = product.priceUnit || product.unit || '';
    return (
      <Text className="card-price">
        <CurrencyRupeeTwoToneIcon fontSize="small" />
        {product.price}
        {displayUnit && (
          <Text as="span" className="price-unit">
            /{displayUnit}
          </Text>
        )}
      </Text>
    );
  };
  
  // Compute discount if fields exist (for chemical/store products)
  const getDiscountPercent = () => {
    const mrp = product.mrp ?? product?.variants?.[0]?.mrp;
    const sell = product.sellingPrice ?? product?.variants?.[0]?.sellingPrice;
    const stored = product.discount ?? product?.variants?.[0]?.discount;
    if (typeof stored === 'number') return stored.toFixed(0);
    if (mrp && sell && mrp > 0) {
      const d = ((mrp - sell) / mrp) * 100;
      return Math.max(0, Math.min(100, d)).toFixed(0);
    }
    return null;
  };
  const discount = getDiscountPercent();
  
  // Function to get optimized image URL
  const getOptimizedImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // If it's already an optimized URL or not from our domain, return as is
    if (imageUrl.includes('?') || !imageUrl.includes('agromanch.com')) {
      return imageUrl;
    }
    
    // Add optimization parameters for our images
    return `${imageUrl}?w=300&h=200&fit=crop&auto=format`;
  };
  
  const primaryImageUrl = product.productpic1 || (product.images && product.images.length > 0 ? product.images[0] : null);
  const optimizedImageUrl = getOptimizedImageUrl(primaryImageUrl);

  return (
    <Link to={`/preview_ad/${product._id}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <Card 
        h="100%" 
        display="flex" 
        flexDirection="column" 
        borderRadius="lg" 
        overflow="hidden" 
        boxShadow="none"
        className="custom-product-card compact-store-card"
        _hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
        transition="all 0.3s ease"
      >
        <Box 
          position="relative" 
          width="100%" 
          className="card-img-container"
        >
          {!imageLoaded && !imageError && (
            <Box className="card-img-placeholder">
              <Box className="card-img-loading" />
            </Box>
          )}
          
          <Image
            src={optimizedImageUrl}
            alt={product.subcategory || product.title}
            className={`card-img ${imageLoaded ? 'card-img-loaded' : ''}`}
            fallbackSrc="https://via.placeholder.com/300x200?text=Product+Image"
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              setImageError(true);
              // If productpic1 fails, try using the first image from images array
              if (product.images && product.images.length > 0 && e.target.src !== product.images[0]) {
                e.target.src = product.images[0];
              } else {
                // If both fail, use the fallback image
                e.target.src = "https://via.placeholder.com/300x200?text=Product+Image";
              }
            }}
            style={{ display: imageError || imageLoaded ? 'block' : 'none' }}
          />
          
          {discount !== null && (
            <Badge 
              position="absolute" 
              top="8px" 
              right="8px" 
              colorScheme="red"
              borderRadius="full"
              px={3}
              py={1}
              fontSize="0.75rem"
            >
              {discount}% OFF
            </Badge>
          )}
          {product.quality && (
            <Badge 
              position="absolute" 
              className={`card-badge ${qualityClass}`}
            >
              Grade {product.quality}
            </Badge>
          )}
        </Box>
        
        <CardBody flex="1" className="card-body">
          <Stack spacing="1">
            <Text className="card-title" noOfLines={1}>
              {product.title}
            </Text>
            
            <Flex gap={1} flexWrap="wrap">
              {product.catagory && (
                <Badge className="card-category">
                  {product.catagory}
                </Badge>
              )}

              {product.variety && (
                <Badge className="card-category" colorScheme="purple" fontSize="xs">
                  {product.variety}
                </Badge>
              )}
            </Flex>
            
            <Flex justify="space-between" align="center" mt="auto">
              {formatPriceWithUnit()}
            </Flex>
          </Stack>
        </CardBody>
      </Card>
    </Link>
  );
}